import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

export interface CurrentUser {
  user_id: number;
  email: string;
  roles_mask: number;
  roles: string[];
  permissions: string[];
  default_route?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private refreshTokenUrl = '/api/refresh_token.php';
  private currentUserUrl = '/api/me.php';
  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  private currentUserRequest?: Observable<CurrentUser | null>;
  readonly fallbackDefaultRoute = '/dashboard';
  lastUrl: string | null = null;

  constructor(private http: HttpClient, private router: Router) {}

  currentUser$ = this.currentUserSubject.asObservable();

  refreshAccessToken(): Observable<{ access_token: string }> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return throwError(() => new Error('Refresh token not found'));
    }

    const data = { refresh_token: refreshToken };
    return this.http.post<{ access_token: string }>(this.refreshTokenUrl, data);
  }

  loadCurrentUser(force = false): Observable<CurrentUser | null> {
    if (!this.isLoggedIn()) {
      this.clearCurrentUser();
      return of(null);
    }

    if (!force) {
      const currentUser = this.currentUserSubject.value;
      if (currentUser) {
        return of(currentUser);
      }

      if (this.currentUserRequest) {
        return this.currentUserRequest;
      }
    }

    this.currentUserRequest = this.http.get<CurrentUser>(this.currentUserUrl).pipe(
      tap((currentUser) => {
        this.currentUserSubject.next(currentUser);
      }),
      map((currentUser) => currentUser ?? null),
      catchError((error) => {
        if (error.status === 401) {
          this.clearCurrentUser();
        }

        return throwError(() => error);
      }),
      finalize(() => {
        this.currentUserRequest = undefined;
      }),
      shareReplay(1)
    );

    return this.currentUserRequest;
  }

  ensureCurrentUserLoaded(): Observable<CurrentUser | null> {
    return this.currentUserSubject.value ? of(this.currentUserSubject.value) : this.loadCurrentUser();
  }

  getCurrentUserSnapshot(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  normalizeDefaultRoute(defaultRoute: unknown): string {
    const defaultRouteText = String(defaultRoute ?? '').trim();

    if (!defaultRouteText) {
      return '';
    }

    if (
      defaultRouteText.includes('://')
      || defaultRouteText.startsWith('//')
      || defaultRouteText.includes('\\')
      || defaultRouteText.includes('..')
    ) {
      return '';
    }

    const normalizedRoute = defaultRouteText.startsWith('/') ? defaultRouteText : `/${defaultRouteText}`;

    return normalizedRoute === '/login' ? '' : normalizedRoute;
  }

  getDefaultRoute(currentUser: CurrentUser | null = this.currentUserSubject.value): string {
    return this.normalizeDefaultRoute(currentUser?.default_route) || this.fallbackDefaultRoute;
  }

  hasPermission(permission: string): boolean {
    return this.hasAllPermissions([permission]);
  }

  hasRole(role: string): boolean {
    return this.hasAllRoles([role]);
  }

  hasAllRoles(roles: string[]): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser) {
      return false;
    }

    const userRoles = currentUser.roles.map((role) => role.toUpperCase());
    return roles.every((role) => userRoles.includes(role.toUpperCase()));
  }

  hasAnyRole(roles: string[]): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser) {
      return false;
    }

    const userRoles = currentUser.roles.map((role) => role.toUpperCase());
    return roles.some((role) => userRoles.includes(role.toUpperCase()));
  }

  hasAllPermissions(permissions: string[]): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser) {
      return false;
    }

    return permissions.every((permission) => currentUser.permissions.includes(permission));
  }

  hasAnyPermission(permissions: string[]): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser) {
      return false;
    }

    return permissions.some((permission) => currentUser.permissions.includes(permission));
  }

  setSessionTokens(accessToken: string, refreshToken?: string): void {
    localStorage.setItem('access_token', accessToken);

    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }

    this.clearCurrentUser();
  }

  updateAccessToken(accessToken: string): void {
    this.setSessionTokens(accessToken);
  }

  clearCurrentUser(): void {
    this.currentUserSubject.next(null);
    this.currentUserRequest = undefined;
  }

  logout() {
    // Clear tokens from local storage and navigate to the login page
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('menuData');
    this.clearCurrentUser();
    this.router.navigate(['/login']);
  }

  isLoggedIn(): boolean {
    const accessToken = localStorage.getItem('access_token');
    return accessToken !== null;
  }
}
